<template>
  <section class="page-shell dashboard-page">
    <div class="page-heading dashboard-heading">
      <div>
        <h1>Central de validação documental</h1>
        <p>Valide minutas, contratos, pareceres e petições antes do uso externo, com revisão humana, políticas e evidências prontas.</p>
        <div class="dashboard-heading-actions">
          <v-btn color="primary" :to="{ name: 'documents', query: { create: 'manual' } }">
            <v-icon left small>mdi-file-plus-outline</v-icon>
            Criar documento
          </v-btn>
          <v-btn outlined color="primary" :to="{ name: 'document-generator' }">
            <v-icon left small>mdi-creation</v-icon>
            Gerar com IA
          </v-btn>
        </div>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <div class="kpi-grid">
      <article v-for="metric in metrics" :key="metric.label" class="metric-card">
        <span>{{ metric.label }}</span>
        <strong>{{ metric.value }}</strong>
        <small>{{ metric.caption }}</small>
      </article>
    </div>

    <div class="work-grid">
      <section class="data-panel dashboard-review-panel">
        <div class="panel-title">
          <h2>Revisões humanas em andamento</h2>
          <v-btn small text color="primary" :to="{ name: 'requests' }">
            <v-icon left small>mdi-open-in-new</v-icon>
            Abrir
          </v-btn>
        </div>
        <table v-if="requests.length" class="dashboard-review-table">
          <thead>
            <tr>
              <th>Cliente/caso</th>
              <th>Uso jurídico</th>
              <th>Status</th>
              <th>Confidencialidade</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in requests" :key="item.id">
              <td>
                <strong class="review-main-text">{{ item.client_name || 'Uso interno' }}</strong>
                <div class="table-caption">{{ item.matter_reference || 'Sem caso informado' }}</div>
              </td>
              <td>
                <span class="review-secondary-text">{{ item.use_case_type || (item.tool ? item.tool.name : 'Documento') }}</span>
              </td>
              <td>
                <v-chip small :color="requestStatusColor(item.status)" text-color="white">
                  {{ requestStatusLabel(item.status) }}
                </v-chip>
              </td>
              <td>
                <span class="review-secondary-text">{{ confidentialityLabel(item.confidentiality_level) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="!loading" class="dashboard-empty-state dashboard-empty-state--inline">
          <v-icon>mdi-account-check-outline</v-icon>
          <div>
            <strong>Nenhuma revisão humana pendente</strong>
            <span>Quando um documento for enviado para revisão, ele aparece aqui com status, uso e confidencialidade.</span>
          </div>
        </div>
      </section>

      <section class="data-panel">
        <div class="panel-title">
          <h2>Políticas ativas</h2>
          <v-btn small text color="primary" :to="{ name: 'policies' }">Abrir</v-btn>
        </div>
        <div v-for="policy in policies" :key="policy.id" class="stack-item">
          <strong>{{ policy.title }}</strong>
          <span>{{ policyCategoryLabel(policy.category) }}</span>
          <v-chip x-small :color="policyStatusColor(policy.status)" text-color="white">{{ policyStatusLabel(policy.status) }}</v-chip>
        </div>
        <div v-if="!loading && !policies.length" class="dashboard-empty-state dashboard-empty-state--inline">
          <v-icon>mdi-shield-check-outline</v-icon>
          <div>
            <strong>Nenhuma política publicada</strong>
            <span>Publique políticas para orientar validações e revisões.</span>
          </div>
        </div>
      </section>
    </div>

    <div class="work-grid">
      <section class="data-panel">
        <div class="panel-title">
          <h2>Políticas essenciais</h2>
          <v-btn small text color="primary" :to="{ name: 'policies' }">Configurar</v-btn>
        </div>
        <div class="attention-list">
          <article class="attention-item">
            <v-icon>mdi-account-check-outline</v-icon>
            <div>
              <strong>Revisão humana obrigatória</strong>
              <p>Documentos externos precisam de responsável identificado antes de envio ou protocolo.</p>
            </div>
          </article>
          <article class="attention-item">
            <v-icon>mdi-account-lock-outline</v-icon>
            <div>
              <strong>Dados sensíveis e cliente</strong>
              <p>Conteúdos com CPF, CNPJ, valores, estratégia ou cliente exigem minimização e decisão.</p>
            </div>
          </article>
          <article class="attention-item">
            <v-icon>mdi-gavel</v-icon>
            <div>
              <strong>Petições e pareceres</strong>
              <p>Fontes jurídicas e fundamentos devem ser conferidos antes de protocolo, envio ou assinatura.</p>
            </div>
          </article>
        </div>
      </section>

      <section class="data-panel dashboard-attention-panel">
        <div class="panel-title">
          <h2>Fila de atenção</h2>
          <v-btn small text color="primary" :to="{ name: 'requests' }">
            <v-icon left small>mdi-format-list-checks</v-icon>
            Ver documentos
          </v-btn>
        </div>
        <div class="attention-list">
          <article v-if="sensitiveDocumentsTotal" class="attention-item">
            <v-icon>mdi-shield-alert-outline</v-icon>
            <div>
              <strong>{{ sensitiveDocumentsTotal }} documento(s) com informação sensível</strong>
              <p>Abra a tela de revisões para verificar quais documentos exigem atenção antes de liberar ou arquivar.</p>
            </div>
          </article>

          <article v-for="item in attentionItems" :key="item.title" class="attention-item">
            <v-icon>{{ item.icon }}</v-icon>
            <div>
              <strong>{{ item.title }}</strong>
              <p>{{ item.description }}</p>
            </div>
          </article>
        </div>
        <div v-if="!loading && !attentionItems.length && !sensitiveDocumentsTotal" class="dashboard-empty-state dashboard-empty-state--inline">
          <v-icon>mdi-check-circle-outline</v-icon>
          <div>
            <strong>Fila limpa</strong>
            <span>Nenhuma ação crítica no momento.</span>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

export default {
  name: 'DashboardPage',
  data () {
    return {
      loading: false,
      error: '',
      summary: {
        metrics: {},
        tools: [],
        requests: [],
        policies: [],
        events: []
      }
    }
  },
  computed: {
    metrics () {
      const data = this.summary.metrics || {}
      return [
        { label: 'Revisões pendentes', value: data.pending_requests || 0, caption: 'Aguardando decisão humana' },
        { label: 'Documentos sensíveis', value: data.sensitive_requests || 0, caption: 'Cliente/caso confidencial' },
        { label: 'Evidências do mês', value: data.evidences_this_month || 0, caption: 'Geradas automaticamente' },
        { label: 'Políticas publicadas', value: data.published_policies || 0, caption: 'Regras em vigor' }
      ]
    },
    tools () {
      return this.summary.tools || []
    },
    requests () {
      return this.summary.requests || []
    },
    policies () {
      return this.summary.policies || []
    },
    sensitiveDocumentsTotal () {
      return (this.summary.metrics && this.summary.metrics.sensitive_requests) || 0
    },
    attentionItems () {
      const data = this.summary.metrics || {}
      const items = []

      if ((data.pending_requests || 0) > 0) {
        items.push({
          icon: 'mdi-clipboard-clock-outline',
          title: `${data.pending_requests} caso(s) aguardando decisão`,
          description: 'Revise documentos pendentes antes que sejam enviados ao cliente, contraparte ou processo.'
        })
      }

      if (!(data.published_policies || 0)) {
        items.push({
          icon: 'mdi-shield-check-outline',
          title: 'Nenhuma política publicada',
          description: 'Publique regras simples de uso para orientar advogados, paralegais e operação.'
        })
      }

      return items.slice(0, 4)
    },
    primaryActionRoute () {
      if ((this.summary.metrics && this.summary.metrics.pending_requests) > 0) return 'requests'
      return 'policies'
    },
    primaryActionLabel () {
      if ((this.summary.metrics && this.summary.metrics.pending_requests) > 0) return 'Revisar'
      return 'Políticas'
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/dashboard')
        this.summary = data
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar a visão geral.')
      } finally {
        this.loading = false
      }
    },
    requestStatusLabel (status) {
      return { draft: 'Rascunho', submitted: 'Enviado', in_review: 'Em revisão', approved: 'Aprovado', rejected: 'Reprovado' }[status] || status
    },
    requestStatusColor (status) {
      return { draft: 'grey', submitted: 'info', in_review: 'info', approved: 'success', rejected: 'error' }[status] || 'grey'
    },
    confidentialityLabel (level) {
      return { public: 'Público', internal: 'Interno', confidential: 'Confidencial', restricted: 'Restrito' }[level] || level || '-'
    },
    policyCategoryLabel (category) {
      return {
        confidentiality: 'Confidencialidade',
        human_review: 'Revisão humana',
        personal_data: 'Dados pessoais',
        legal_work_product: 'Documentos jurídicos',
        external_use: 'Uso externo'
      }[category] || 'Política operacional'
    },
    policyStatusLabel (status) {
      return { draft: 'Rascunho', published: 'Publicada', archived: 'Arquivada' }[status] || status || '-'
    },
    policyStatusColor (status) {
      return { draft: 'grey', published: 'success', archived: 'grey' }[status] || 'grey'
    },
    contractLabel (status) {
      return { not_reviewed: 'Não revisado', reviewed: 'Revisado', approved: 'Aprovado', rejected: 'Reprovado' }[status] || status || '-'
    },
    riskLabel (risk) {
      return { not_classified: 'Não classificado', low: 'Baixo', medium: 'Médio', high: 'Alto', critical: 'Crítico' }[risk] || risk
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-page {
  padding-top: 36px;
}

.dashboard-heading {
  align-items: flex-start;
  margin-bottom: 26px;
}

.dashboard-heading > div:first-child {
  min-width: 0;
  max-width: 1120px;
}

.dashboard-heading-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  margin-top: 18px;
}

.dashboard-heading-actions .v-btn {
  height: 38px !important;
  min-width: 142px !important;
  padding: 0 13px !important;
  box-shadow: none !important;
}

.dashboard-heading-actions .v-btn ::v-deep .v-btn__content {
  font-size: 13px;
  font-weight: 850;
  letter-spacing: 0;
  line-height: 1;
  white-space: nowrap;
}

.dashboard-review-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.dashboard-review-table th,
.dashboard-review-table td {
  padding: 13px 10px;
  border-bottom: 1px solid #e3e8e6;
  text-align: left;
  vertical-align: middle;
}

.dashboard-review-table th:first-child,
.dashboard-review-table td:first-child {
  padding-left: 0;
}

.dashboard-review-table th:last-child,
.dashboard-review-table td:last-child {
  padding-right: 0;
}

.dashboard-review-table th {
  color: #6c7d78;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .06em;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
}

.dashboard-review-table tbody tr:last-child td {
  border-bottom: 0;
}

.review-main-text {
  display: block;
  color: #101918;
  font-size: 14px;
  font-weight: 850;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.review-secondary-text {
  display: block;
  color: #475467;
  font-size: 13px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.dashboard-empty-state {
  color: #667085;
}

.dashboard-empty-state--inline {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  min-height: 112px;
  padding: 22px 0 6px;
  border-top: 1px solid #e3e8e6;
  color: #667085;
}

.dashboard-empty-state .v-icon {
  color: #0f4efb;
  font-size: 30px;
}

.dashboard-empty-state--inline .v-icon {
  justify-self: center;
  margin-top: 2px;
}

.dashboard-empty-state strong {
  display: block;
  color: #101918;
  font-size: 15px;
  font-weight: 850;
  line-height: 1.25;
}

.dashboard-empty-state span {
  display: block;
  max-width: 420px;
  margin-top: 5px;
  color: #667085;
  font-size: 13px;
  line-height: 1.45;
}

.dashboard-attention-panel .attention-list {
  gap: 0;
}

.dashboard-attention-panel .attention-item {
  align-items: center;
  padding: 16px 0;
}

@media (max-width: 1180px) {
  .dashboard-page {
    padding-top: 32px;
  }
}

@media (max-width: 720px) {
  .dashboard-heading-actions {
    flex-wrap: wrap;
    width: 100%;
  }

  .dashboard-heading-actions .v-btn {
    flex: 1 1 100%;
  }

  .dashboard-review-table {
    min-width: 680px;
  }

  .dashboard-review-panel {
    overflow-x: auto;
  }
}
</style>
